// HQ onboards organisations (F-HQ-02; spec/14 §3.1, spec/07 §7, spec/03 §11, spec/04 §2).
//
//   GET    /v1/hq/organizations                                      every organisation, newest first    hq:organizations
//   POST   /v1/hq/organizations                                      invite a café: create it, invite    hq:organizations
//                                                                    its owner
//   GET    /v1/hq/organizations/{organizationId}                     one                                 hq:organizations
//   GET    /v1/hq/organizations/{o}/owner                            its owner, or its owner invitation  hq:organizations
//   POST   /v1/hq/organizations/{organizationId}/owner-invitations   invite an owner while it has none   hq:organizations
//   POST   /v1/hq/organizations/{o}/owner-invitations/{i}/resend     a fresh one to the same contact     hq:organizations
//   POST   /v1/hq/organizations/{o}/owner-invitations/{i}/link       the link of a pending one           hq:organizations
//   DELETE /v1/hq/organizations/{o}/owner-invitations/{i}            withdraw a pending one              hq:organizations
//
// **Invite a café, light** (ADR-0046, the product owner's decision, 2026-10-08). HQ invites with the
// café's business name and its owner's email or phone, and nothing else is required: the slug is
// written from the name, the kind is `independent` and the HQ access level `full` unless HQ says
// otherwise, and the legal name, PAN and registered address are the owner's to complete during setup
// (`PATCH /v1/organizations/{o}/business-details`) — HQ may still prefill anything it knows. Until the
// legal name and the registered address are recorded, no venue of the organisation records a GST
// registration, so none issues a GST document (`409 business-details-required`, spec/06 §10.3).
//
// **HQ sees what became of the owner's invitation**: invited (when, by whom, how, and whether its
// message went), expired, withdrawn, or joined (who, when). It can send a fresh one to the same
// contact, withdraw a pending one, and read a pending one's link to pass on. The owner invitation is
// HQ's onboarding record — HQ made it — so reading its status writes no audit row, as reading the
// record writes none; every change is audited in the organisation's log, and so is reading a link.
//
// **An organisation is created by HQ and nobody else.** better-auth's own `POST /organization/create`
// stays switched off: it would let anyone found an organisation, make its caller the owner, and
// write nothing our audit log could join.
//
// **Its owner is invited, never made.** HQ is not a member of the organisation it creates, and it
// gains no membership by creating one. The owner is invited through the staff invitation machinery
// every other member joins by (F-IAM-08, spec/07 §6.5) — the same `staff_invitation` row, the same
// signed link, the same N-25 message, the same acceptance: signed in with the invited phone number or
// the invited address, verified, and with the second factor the owner role needs. An email invitation
// goes out today; a phone one waits for SMS and WhatsApp (F-CMP-09), and `delivery` says `not_sent`
// with the link for HQ to pass on. The one rule HQ bends is spec/07 §6.5's "only an owner grants the
// owner role": HQ may invite an owner **while the organisation has none** (spec/14 §3.1), and only then.
//
// **PAN is optional** when HQ creates one (the product owner, 2026-09-22): a pilot cafe may not have it
// to hand. Given, it must be a PAN; left out, the record shows `null` until editing the record adds it.
//
// **What HQ reads here is the organisation's record** — its names, kind, PAN, registered address, HQ
// access level and status — which is HQ's own commercial record (spec/14 §3.1), not the organisation's
// customer data. Reading it writes no audit row. The organisation's rows — its players, members,
// money — are not here: HQ reaches them through an audited door, as far as `hqAccess` allows, which is
// `full` unless HQ records an agreement that limits it (spec/07 §7).
//
// Every write is audited in the new organisation's own log, one row per action (spec/07 §11.1): the
// organisation's creation, and each invitation made or withdrawn. Platform scope: every route needs a
// platform role holding `hq:organizations` and a second factor on the session.
//
// Not here yet, and why (see the pull request): plans, subscriptions and entitlements (F-HQ-07/08/10),
// networks (F-HQ-05), suspending and reactivating (F-HQ-06, #205), the onboarding checklist
// (F-HQ-03), and HQ editing the record (`PATCH`), which comes with the organisation page.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { InvitationIdSchema, OrganizationIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_ORGANIZATIONS } from "../permissions/index.js";
import { AddressSchema } from "../primitives/address.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { PhoneNumberSchema } from "../primitives/phone-number.js";
import { CurrencyCodeSchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { CreatedInvitationSchema, InvitationChannelSchema, InvitationMessageChannelSchema, InvitationStatusSchema, } from "./members.js";
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
/**
 * spec/04 §2, spec/07 §7, [ADR-0019](../adr/0019-multi-tenant-organisations-and-franchise-tree.md).
 * Where an organisation sits in the franchise tree: a **franchisor** owns venues directly and is the
 * `franchisorOrganizationId` of its franchisees; a **franchisee** is its own legal entity linked to a
 * franchisor; an **independent** stands alone — no franchisor and no franchisees — and may later
 * become a franchisor with no migration. Supersedes the retired `operator_owned`/`franchise` kinds: a
 * brand's own venues are now the franchisor's own venues.
 */
export const ORGANIZATION_KINDS = ['franchisor', 'franchisee', 'independent'];
export const OrganizationKindSchema = z.enum(ORGANIZATION_KINDS).openapi('OrganizationKind', {
    description: '`franchisor`: a brand that owns venues and has franchisees. `franchisee`: a franchisor’s own-entity outlet, linked by `franchisorOrganizationId`. `independent`: a standalone owner.',
});
/**
 * spec/07 §7: what an organisation's agreement lets HQ see. `full` — row-level too, every read of its
 * customer data audited with a reason — is the default: the product owner chose complete HQ access
 * (2026-09-22). `aggregate_only`, aggregates and no row-level customer data, is what HQ records when an
 * agreement limits it. Retained unchanged by ADR-0019 Phase 1; the Provider/Brand-HQ split that
 * reframes it is a later phase.
 */
export const HQ_ACCESS_LEVELS = ['aggregate_only', 'full'];
export const HqAccessSchema = z.enum(HQ_ACCESS_LEVELS).openapi('HqAccess', {
    description: '`aggregate_only`: HQ sees aggregates and no row-level customer data. `full`: row-level too, each read audited.',
});
/**
 * The HQ access level an organisation gets when HQ does not name one, and every organisation that
 * predates onboarding has: `full`. HQ has complete access unless an agreement says otherwise (the
 * product owner's decision, 2026-09-22).
 */
export const HQ_ACCESS_DEFAULT = 'full';
/**
 * `pending` (registered, awaiting Provider approval — ADR-0019 §6), `active`, or `suspended`
 * (spec/14 §4.4). ADR-0019 Phase 1 added the `pending` value; Phase 3 (`POST /v1/hq/organizations/pending`
 * and `POST /v1/hq/organizations/{o}/approval`, below) lands an organisation `pending` and moves it to
 * `active` on Provider approval — or `suspended` on decline. The direct create (`POST /v1/hq/organizations`)
 * still lands `active`: a customer the Provider onboards itself is approved by the act of onboarding it.
 * Suspending and reactivating an established organisation is F-HQ-06 (#205).
 */
export const ORGANIZATION_STATUSES = ['pending', 'active', 'suspended'];
export const OrganizationStatusSchema = z.enum(ORGANIZATION_STATUSES).openapi('OrganizationStatus');
/** An organisation's slug: lower-kebab, unique across the Platform. */
export const OrganizationSlugSchema = z
    .string()
    .min(3)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lower-case letters, digits and single hyphens, e.g. playground-gaming')
    .openapi('OrganizationSlug', { example: 'playground-gaming' });
/**
 * India's Permanent Account Number: five letters, four digits, a letter, upper case. GST registration
 * is not asked of an organisation — it is per venue, a tax mode and a GSTIN where the venue has one
 * (spec/06 §10.3).
 */
export const PanSchema = z
    .string()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'A PAN is five capital letters, four digits and a capital letter, e.g. AAACP1234Q')
    .openapi('Pan', { example: 'AAACP1234Q' });
/** Who to invite as the organisation's owner: a phone number or an email address, exactly one. */
export const OwnerInvitationContactSchema = z
    .object({
    phoneNumber: PhoneNumberSchema.optional(),
    email: z.string().trim().min(3).max(254).regex(/^[^\s@]+@[^\s@]+$/, 'An email address').optional(),
})
    .strict()
    .refine((body) => (body.phoneNumber === undefined) !== (body.email === undefined), {
    message: 'Give a phone number or an email address — exactly one.',
    path: ['phoneNumber'],
})
    .openapi('OwnerInvitationContact', {
    description: 'The person to invite as owner, by phone or by email — exactly one.',
});
export const HqOrganizationSchema = z
    .object({
    id: OrganizationIdSchema,
    /** The trade name: what the organisation is called day to day, on its console and its venues. */
    name: z.string(),
    slug: z.string(),
    /**
     * The registered legal entity. `null` until it is recorded: HQ invites a café with its business name
     * alone, and the owner completes it during setup (ADR-0046) — or an organisation that predates HQ
     * onboarding. While it or the registered address is `null`, no venue records a GST registration.
     */
    legalName: z.string().nullable(),
    kind: OrganizationKindSchema,
    /**
     * The franchisor this organisation belongs to (ADR-0019): `null` for a top-level org (a franchisor
     * or an independent), the brand's organisation id for a franchisee. A link, not an access grant —
     * it never lets one organisation read another's rows (isolation stays keyed on `organization_id`).
     */
    franchisorOrganizationId: OrganizationIdSchema.nullable(),
    /** `null` until HQ records it: a pilot cafe may not have it to hand at creation (the product owner, 2026-09-22). */
    pan: z.string().nullable(),
    /** `null` until it is recorded — by HQ when it invites the café, or by the owner during setup (ADR-0046). */
    registeredAddress: AddressSchema.nullable(),
    hqAccess: HqAccessSchema,
    status: OrganizationStatusSchema,
    /** What its wallets, prices and payouts are in (spec/03 §10): the deployment's default, fixed at creation. */
    currency: CurrencyCodeSchema,
    createdAt: IsoDateTimeSchema,
})
    .openapi('HqOrganization', {
    description: 'An organisation’s record, as HQ holds it (spec/14 §3.1, spec/04 §2).',
});
// ---------------------------------------------------------------------------------------------
// The owner, as HQ sees it (ADR-0046, spec/14 §3.1)
// ---------------------------------------------------------------------------------------------
/**
 * Where an organisation's owner is: `joined` (an active owner is on its staff), `invited` (the newest
 * owner invitation is pending and not expired), `expired` (it is pending past its seven days),
 * `withdrawn` (it was withdrawn and no newer one was made), or `none` (no owner invitation was made).
 */
export const HQ_OWNER_STATUSES = ['joined', 'invited', 'expired', 'withdrawn', 'none'];
export const HqOwnerStatusSchema = z.enum(HQ_OWNER_STATUSES).openapi('HqOwnerStatus', {
    description: '`joined`: an active owner is on its staff. `invited`: the newest owner invitation is waiting. `expired`: it ' +
        'passed its seven days unused. `withdrawn`: it was withdrawn. `none`: no owner invitation was made.',
});
/** The owner column of HQ's list: the status, and when it came to be. */
export const HqOwnerSummarySchema = z
    .object({
    status: HqOwnerStatusSchema,
    /**
     * `joined`: when the owner joined. `invited`: when the invitation was made. `expired`: when it expired.
     * `withdrawn`: when it was withdrawn. `none`: `null`.
     */
    at: IsoDateTimeSchema.nullable(),
})
    .openapi('HqOwnerSummary', { description: 'Whether the organisation’s owner has joined, and since when.' });
/**
 * One row of HQ's list: the record and its owner's status. A fresh object rather than
 * `HqOrganizationSchema.extend(…)`: extending a registered component emits `allOf`, which the client
 * generators refuse (tools/src/codegen).
 */
export const HqOrganizationListItemSchema = z
    .object({ ...HqOrganizationSchema.shape, owner: HqOwnerSummarySchema })
    .openapi('HqOrganizationListItem', { description: 'An organisation’s record, and whether its owner has joined.' });
export const HqOrganizationListSchema = paginated(HqOrganizationListItemSchema, 'HqOrganizationList');
/** `not_joined`: only the organisations whose owner has not joined yet — invited, expired, withdrawn or none. */
export const HQ_ORGANIZATION_OWNER_FILTERS = ['not_joined'];
export const HqOrganizationListQuerySchema = PageQuerySchema.extend({
    owner: z
        .enum(HQ_ORGANIZATION_OWNER_FILTERS)
        .optional()
        .openapi({ description: '`not_joined`: only organisations with no active owner yet — the cafés still to finish joining.' }),
})
    // Unknown parameters are refused, never turned into a filter (spec/08 §2.4).
    .strict()
    .openapi('HqOrganizationListQuery');
/**
 * Whether the owner invitation's message (N-25) went: `sent` once a provider took it, `not_sent` when
 * none could carry it or the one that could failed, `unknown` for an invitation made before the outcome
 * was recorded.
 */
export const OWNER_INVITATION_DELIVERY_STATUSES = ['sent', 'not_sent', 'unknown'];
export const HqOwnerInvitationSchema = z
    .object({
    id: InvitationIdSchema,
    channel: InvitationChannelSchema,
    /** The number or address invited, masked. */
    destination: z.string(),
    status: InvitationStatusSchema,
    invitedAt: IsoDateTimeSchema,
    /** Who invited: their name as their account gives it, or `null` when it gives none. */
    invitedByName: z.string().nullable(),
    expiresAt: IsoDateTimeSchema,
    delivery: z
        .object({
        status: z.enum(OWNER_INVITATION_DELIVERY_STATUSES),
        /** The channel it went by; `null` unless `sent`. */
        channel: InvitationMessageChannelSchema.nullable(),
        /** When the message was attempted; `null` when `unknown`. */
        at: IsoDateTimeSchema.nullable(),
    })
        .openapi('HqOwnerInvitationDelivery'),
    /** When it was accepted; `null` unless `accepted`. */
    acceptedAt: IsoDateTimeSchema.nullable(),
    /** When it was withdrawn; `null` unless `revoked`. */
    revokedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('HqOwnerInvitation', { description: 'An owner invitation, as HQ follows it (ADR-0046). Never its link.' });
/** The owner who joined: their name, their contacts masked, and when they joined. */
export const HqOrganizationOwnerPersonSchema = z
    .object({
    name: z.string().nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    joinedAt: IsoDateTimeSchema,
})
    .openapi('HqOrganizationOwnerPerson', { description: 'The organisation’s owner, as HQ sees them: contacts masked.' });
export const HqOrganizationOwnerSchema = z
    .object({
    status: HqOwnerStatusSchema,
    /**
     * The newest owner invitation HQ made, whatever became of it, while no owner has joined; `null` when HQ made
     * none, and once an owner has joined (from then on the café's owners invite owners, and those are its own).
     */
    invitation: HqOwnerInvitationSchema.nullable(),
    /** The owner who joined first and is still active; `null` until one has joined. Contacts masked. */
    owner: HqOrganizationOwnerPersonSchema.nullable(),
})
    .openapi('HqOrganizationOwner', { description: 'Whether the organisation’s owner has joined, and its newest owner invitation.' });
/** A pending owner invitation's link, for HQ to pass on (ADR-0046): it works only for the person invited. */
export const HqOwnerInvitationLinkSchema = z
    .object({ acceptUrl: z.string(), expiresAt: IsoDateTimeSchema })
    .openapi('HqOwnerInvitationLink', {
    description: 'The link only the invited person can use: accepting needs a session signed in with the invited contact.',
});
/** The kind an invited café is when HQ does not name one (ADR-0046): a standalone owner. */
export const INVITED_ORGANIZATION_KIND_DEFAULT = 'independent';
/**
 * Inviting a café (ADR-0046): the business (trade) name and the owner's email or phone are all HQ must
 * give. Everything else is optional — HQ may prefill what it knows, and the owner completes the legal
 * name, PAN and registered address during setup.
 */
export const CreateHqOrganizationSchema = z
    .object({
    /** The trade name: the café's business name. */
    name: z.string().trim().min(1).max(120),
    /**
     * Omit it and the server writes one from the name exactly as `@clavian/domain`'s `organizationSlugFrom` does
     * (`Playground Gaming` → `playground-gaming`). Taken — given or written — is `409 slug-taken`, never a
     * suffixed second one, so a retried invitation never makes a second organisation; a name that makes
     * no slug (too short once written) is `400` on `slug`.
     */
    slug: OrganizationSlugSchema.optional(),
    /** Optional: the owner completes it during setup when HQ does not have it (ADR-0046). */
    legalName: z.string().trim().min(1).max(200).optional(),
    /** Omit it for `independent`, a standalone owner. */
    kind: OrganizationKindSchema.optional(),
    /**
     * Attach the new organisation to a franchisor (ADR-0019): omit for a top-level org. When given, it
     * must be an existing top-level `franchisor`, and the new organisation becomes its franchisee. The
     * link is data, not an access grant — it grants the franchisor no access to this organisation's rows.
     */
    franchisorOrganizationId: OrganizationIdSchema.optional(),
    /**
     * Optional: a pilot cafe may not have its PAN to hand when HQ creates it (the product owner,
     * 2026-09-22). When given, it must be a PAN. Adding it later comes with editing the record.
     */
    pan: PanSchema.optional(),
    /** Optional: the owner completes it during setup when HQ does not have it (ADR-0046). */
    registeredAddress: AddressSchema.optional(),
    /** Omit it for `full`, the default: complete HQ access unless an agreement limits it. */
    hqAccess: HqAccessSchema.optional(),
    /** The person invited as owner. They become the owner when they accept. */
    owner: OwnerInvitationContactSchema,
})
    .strict()
    .openapi('CreateHqOrganization', {
    description: 'A café invited onto the Platform: its business name and its owner’s email or phone, and whatever else HQ knows. ' +
        'Its currency is the deployment’s default (the brand configuration). Plan, subscription and network membership ' +
        'come with their features.',
});
export const CreatedHqOrganizationSchema = z
    .object({
    organization: HqOrganizationSchema,
    /** The owner's invitation, its link, and whether its message went. */
    ownerInvitation: CreatedInvitationSchema,
})
    .openapi('CreatedHqOrganization', { description: 'The organisation just created, and its owner’s invitation.' });
// ---------------------------------------------------------------------------------------------
// Self-serve onboarding, provider-gated (ADR-0019 §6; F-ORG-17 Phase 3, spec/14 §3.1)
// ---------------------------------------------------------------------------------------------
//
// A prospective organisation is **registered `pending`** into the approval queue, and becomes `active`
// only when the Provider **approves** it — or `suspended` when the Provider declines it. A `pending`
// organisation has **no owner and no members** until it is approved: it can be seen and decided on, but
// cannot operate, so no per-request access rule is needed to hold it back. On approval the Provider
// invites its owner through the existing `POST /v1/hq/organizations/{o}/owner-invitations`.
//
// Only a **top-level** organisation is registered this way — a `franchisor` or an `independent`. A
// franchisee is created by its franchisor and inherits that approval (ADR-0019 §6), so it never sits in
// this queue and cannot name a `franchisorOrganizationId` here. The applicant-facing public endpoint (a
// signed-in person creating their own organisation) is the remaining Phase 3 slice; this is the Provider
// intake it lands into.
/** The kinds a pending organisation may be registered as: a top-level org, never a franchisee. */
export const TOP_LEVEL_ORGANIZATION_KINDS = ['franchisor', 'independent'];
export const TopLevelOrganizationKindSchema = z.enum(TOP_LEVEL_ORGANIZATION_KINDS).openapi('TopLevelOrganizationKind', {
    description: '`franchisor` or `independent`. A franchisee is created by its franchisor, not registered here.',
});
export const RegisterPendingOrganizationSchema = z
    .object({
    /** The trade name. */
    name: z.string().trim().min(1).max(120),
    slug: OrganizationSlugSchema,
    legalName: z.string().trim().min(1).max(200),
    kind: TopLevelOrganizationKindSchema,
    /** Optional at registration; adding it later comes with editing the record. When given, it must be a PAN. */
    pan: PanSchema.optional(),
    registeredAddress: AddressSchema,
    /** Omit it for `full`, the default: complete HQ access unless an agreement limits it. */
    hqAccess: HqAccessSchema.optional(),
})
    // No `owner`: a pending organisation gains its owner on approval. No `franchisorOrganizationId`: a
    // franchisee is not registered here. `.strict()` refuses either rather than ignoring it.
    .strict()
    .openapi('RegisterPendingOrganization', {
    description: 'A prospective organisation, registered `pending` into the Provider approval queue (ADR-0019 §6). Its currency is the deployment’s default.',
});
/**
 * The Provider's decision on a `pending` organisation (ADR-0019 §6): **approve** it — it becomes
 * `active` and may be given an owner — or **decline** it, with a reason for the record, which leaves it
 * `suspended` (inert: it has no owner, members or venues). Only a `pending` organisation may be decided.
 *
 * `reason` is required when declining and refused when approving — an approval carries no reason and a
 * decline is never unexplained.
 */
export const ORGANIZATION_DECISIONS = ['approve', 'decline'];
export const OrganizationDecisionSchema = z
    .object({
    decision: z.enum(ORGANIZATION_DECISIONS),
    reason: z
        .string()
        .trim()
        .min(1)
        .max(500)
        .optional()
        .openapi({ description: 'Why the application was declined. Required on `decline`, recorded in the audit log.' }),
})
    .strict()
    .refine((body) => (body.decision === 'decline') === (body.reason !== undefined), {
    message: 'Give a reason when declining, and none when approving.',
    path: ['reason'],
})
    .openapi('OrganizationDecision', { description: 'Approve a pending organisation, or decline it with a reason.' });
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const json = (schema) => ({
    body: { content: { 'application/json': { schema } }, required: true },
});
const forbidden = problemResponse('Not HQ, no `hq:organizations`, or no second factor on the session.', 'forbidden', 'second-factor-required');
const noSuchOrganization = problemResponse('No such organisation.', 'not-found');
export const listHqOrganizationsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations',
    operationId: 'listHqOrganizations',
    tags: ['hq'],
    summary: 'Every organisation on the Platform, newest first',
    description: 'The organisations’ records: names, kind, PAN, registered address, HQ access level and status — and whether each ' +
        'one’s owner has joined (ADR-0046). `owner=not_joined` lists only those whose owner has not joined yet. No ' +
        'organisation’s customer data, and no audit row. Platform scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { query: HqOrganizationListQuerySchema },
    responses: {
        200: jsonResponse(HqOrganizationListSchema, 'A page of organisations.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createHqOrganizationRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations',
    operationId: 'createHqOrganization',
    tags: ['hq'],
    summary: 'Invite a café: create its organisation, and invite its owner',
    description: 'Creates the organisation, `active`, in the deployment’s currency, from its business name and whatever else HQ ' +
        'knows (ADR-0046) — the slug is written from the name when none is given, the kind is `independent` and the HQ ' +
        'access level `full` by default, and the legal name, PAN and registered address may be left for the owner to ' +
        'complete during setup. It invites the owner by phone or email through the staff invitation (spec/07 §6.5): the ' +
        'link works once, for seven days, for someone signed in with that number or address, verified, with a second ' +
        'factor. `ownerInvitation.delivery` says whether the message went, and `acceptUrl` is the link to pass on. HQ ' +
        'gains no membership. A slug already in use — given, or written from the name — is `409 slug-taken`, so a retried ' +
        'create never makes a second organisation. Two audit rows in the new organisation’s log: its creation, and the invitation. Platform ' +
        'scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { ...json(CreateHqOrganizationSchema) },
    responses: {
        201: jsonResponse(CreatedHqOrganizationSchema, 'The organisation, and its owner’s invitation.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        409: problemResponse('Another organisation already has that slug. Or: an invitation to a phone number while phone sign-in is turned off on the Platform (`auth.phoneSignIn`) — email only; nothing was written or sent.', 'slug-taken', 'phone-invitations-off'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getHqOrganizationRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}',
    operationId: 'getHqOrganization',
    tags: ['hq'],
    summary: 'Read one organisation’s record',
    description: 'Platform scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(HqOrganizationSchema, 'The organisation.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchOrganization,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createHqOwnerInvitationRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/owner-invitations',
    operationId: 'createHqOwnerInvitation',
    tags: ['hq'],
    summary: 'Invite an owner to an organisation that has none',
    description: 'For an organisation whose owner has not accepted yet — the invitation expired, or went to the wrong address. ' +
        'Refused with `409` once the organisation has an active owner: from then on its owners invite owners (spec/07 §6.5). ' +
        'Every owner invitation HQ made still pending is withdrawn, so only the newest link works. One audit row per invitation ' +
        'withdrawn and one for the new one, in the organisation’s log. Platform scope: requires `hq:organizations` and a ' +
        'second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OrganizationScopeParamsSchema, ...json(OwnerInvitationContactSchema) },
    responses: {
        201: jsonResponse(CreatedInvitationSchema, 'The invitation, and whether its message went.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchOrganization,
        409: problemResponse('The organisation already has an owner, or that person is already on its staff. Or: an invitation to a phone number while phone sign-in is turned off on the Platform (`auth.phoneSignIn`) — email only; nothing was written or sent.', 'conflict', 'already-a-member', 'phone-invitations-off'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const OwnerInvitationParamsSchema = OrganizationScopeParamsSchema.extend({ invitationId: InvitationIdSchema }).openapi('HqOwnerInvitationParams');
const noSuchOwnerInvitation = problemResponse('No such organisation, or no such owner invitation HQ made to it (an owner invitation its own owners made is not HQ’s).', 'not-found');
/** Withdrawing or handing out the link of an owner invitation, refused: an owner has joined, or it cannot be accepted. */
const ownerInvitationClosed = problemResponse('The organisation has an owner now, or the invitation was already used, withdrawn or has expired.', 'conflict', 'invitation-unavailable');
export const getHqOrganizationOwnerRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/owner',
    operationId: 'getHqOrganizationOwner',
    tags: ['hq'],
    summary: 'Whether the organisation’s owner has joined, and its newest owner invitation',
    description: 'Joined (who, masked, and when), or the newest owner invitation HQ made and what became of it: invited (when, by ' +
        'whom, by email or phone, and whether its message went), expired or withdrawn (ADR-0046). Never the link, never ' +
        'an owner invitation the organisation’s own owners made, and no invitation at all once an owner has joined. The owner ' +
        'invitation is HQ’s onboarding record, so reading it writes no audit row. Platform scope: requires ' +
        '`hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(HqOrganizationOwnerSchema, 'The owner, or the owner invitation.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchOrganization,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const resendHqOwnerInvitationRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/owner-invitations/{invitationId}/resend',
    operationId: 'resendHqOwnerInvitation',
    tags: ['hq'],
    summary: 'Send a fresh owner invitation to the same email or phone',
    description: 'A new invitation, with a new link and seven days, to the contact this owner invitation of HQ’s was for — pending, ' +
        'expired or withdrawn — and every owner invitation HQ made still pending is withdrawn, so only the newest link works ' +
        '(ADR-0046). Refused with `409` once the organisation has an active owner, and for an invitation already ' +
        'accepted. One audit row per invitation withdrawn and one for the new one, in the organisation’s log. Platform ' +
        'scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OwnerInvitationParamsSchema },
    responses: {
        201: jsonResponse(CreatedInvitationSchema, 'The new invitation, its link, and whether its message went.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchOwnerInvitation,
        409: problemResponse('The organisation already has an owner, the invitation was accepted, or that person is already on its staff. ' +
            'Or: an invitation to a phone number while phone sign-in is turned off on the Platform (`auth.phoneSignIn`) — email only; nothing was written or sent.', 'conflict', 'invitation-unavailable', 'already-a-member', 'phone-invitations-off'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const withdrawHqOwnerInvitationRoute = createRoute({
    method: 'delete',
    path: '/v1/hq/organizations/{organizationId}/owner-invitations/{invitationId}',
    operationId: 'withdrawHqOwnerInvitation',
    tags: ['hq'],
    summary: 'Withdraw a pending owner invitation',
    description: 'Its link stops working at once (ADR-0046). Only an owner invitation HQ made, only while the organisation has no ' +
        'active owner (`409 conflict` once one has joined: its owners decide from then on), and only a pending one — not ' +
        'expired, used or already withdrawn — is withdrawn; anything else is `409 invitation-unavailable`. One audit row in ' +
        'the organisation’s log. Platform scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OwnerInvitationParamsSchema },
    responses: {
        204: { description: 'Withdrawn.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchOwnerInvitation,
        409: ownerInvitationClosed,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getHqOwnerInvitationLinkRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/owner-invitations/{invitationId}/link',
    operationId: 'getHqOwnerInvitationLink',
    tags: ['hq'],
    summary: 'The link of a pending owner invitation, to pass on',
    description: 'For when the message did not go, or HQ is asked for it (ADR-0046): the link works only for someone signed in ' +
        'with the invited email or phone, verified. Only an owner invitation HQ made, only while the organisation has no ' +
        'active owner (`409 conflict` once one has joined), and only a pending one — anything else is ' +
        '`409 invitation-unavailable`. A `POST` because handing out a link is recorded: one audit row in the ' +
        'organisation’s log (`hq.owner_invitation.link`). Served `no-store`. Platform scope: requires `hq:organizations` ' +
        'and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OwnerInvitationParamsSchema },
    responses: {
        200: jsonResponse(HqOwnerInvitationLinkSchema, 'The link, and when it stops working.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchOwnerInvitation,
        409: ownerInvitationClosed,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const registerPendingOrganizationRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/pending',
    operationId: 'registerPendingOrganization',
    tags: ['hq'],
    summary: 'Register an organisation awaiting approval',
    description: 'Creates a **top-level** organisation `pending`, in the deployment’s currency, with no owner and no members ' +
        'until it is approved (ADR-0019 §6). One audit row in the new organisation’s log: its creation. Approve or ' +
        'decline it with `POST /v1/hq/organizations/{o}/approval`, then invite its owner. A slug already in use is ' +
        '`409 slug-taken`. Platform scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { ...json(RegisterPendingOrganizationSchema) },
    responses: {
        201: jsonResponse(HqOrganizationSchema, 'The organisation, registered `pending`.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        409: problemResponse('Another organisation already has that slug.', 'slug-taken'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const decideHqOrganizationRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/approval',
    operationId: 'decideHqOrganization',
    tags: ['hq'],
    summary: 'Approve or decline a pending organisation',
    description: 'The Provider’s decision on an organisation awaiting approval (ADR-0019 §6): `approve` moves it `pending → ' +
        'active`; `decline` (with a `reason`) moves it `pending → suspended`. Only a `pending` organisation may be ' +
        'decided — anything already `active` or `suspended` is `409`. One audit row in the organisation’s log, with ' +
        'the status before and after. Platform scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OrganizationScopeParamsSchema, ...json(OrganizationDecisionSchema) },
    responses: {
        200: jsonResponse(HqOrganizationSchema, 'The organisation as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchOrganization,
        409: problemResponse('The organisation is not awaiting approval.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const HQ_ORGANIZATION_ROUTES = [
    listHqOrganizationsRoute,
    createHqOrganizationRoute,
    registerPendingOrganizationRoute,
    getHqOrganizationRoute,
    decideHqOrganizationRoute,
    createHqOwnerInvitationRoute,
    getHqOrganizationOwnerRoute,
    resendHqOwnerInvitationRoute,
    withdrawHqOwnerInvitationRoute,
    getHqOwnerInvitationLinkRoute,
];
//# sourceMappingURL=hq-organizations.js.map